// Ranges and buckets of the cash report. The bucket math works on the
// server's own day strings ("YYYY-MM-DD") via Date.UTC, so no browser
// timezone can shift a bucket; the range builders below speak the
// viewer's local calendar, the frame the report pickers always used.

export interface BucketCents {
  stripeCents: number;
  manualCents: number;
  collectedCents: number;
  returnedCents: number;
  netCents: number;
}

export interface BucketRow extends BucketCents {
  bucket: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A date input mid-edit hands over "" - everything here must shrug,
    not throw, because these run on every render. */
export function isDayString(s: string): boolean {
  return DAY_RE.test(s);
}

function toUtc(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function toDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Every day from `from` to `to`, both inclusive. */
export function enumerateDays(from: string, to: string): string[] {
  if (!isDayString(from) || !isDayString(to)) return [];
  const out: string[] = [];
  for (let t = toUtc(from); t <= toUtc(to); t += DAY_MS) out.push(toDay(t));
  return out;
}

/** Every month ("YYYY-MM") the from..to range touches, both inclusive. */
export function enumerateMonths(from: string, to: string): string[] {
  if (!isDayString(from) || !isDayString(to)) return [];
  const out: string[] = [];
  let [y, m] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

const ZERO: BucketCents = {
  stripeCents: 0,
  manualCents: 0,
  collectedCents: 0,
  returnedCents: 0,
  netCents: 0,
};

/** The server omits quiet buckets; a chart must still show their slots. */
export function zeroFill(keys: string[], rows: Map<string, BucketCents>): BucketRow[] {
  return keys.map((bucket) => ({ bucket, ...(rows.get(bucket) ?? ZERO) }));
}

/** Days from `from` to `to`, both counted; zero for a malformed or
    reversed range. */
export function spanDays(from: string, to: string): number {
  if (!isDayString(from) || !isDayString(to)) return 0;
  return Math.max(0, (toUtc(to) - toUtc(from)) / DAY_MS + 1);
}

/** Today (or the given date) as a day string in the viewer's local
    calendar. */
export function localDayKey(d = new Date()): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The last n days ending today. */
export function lastDaysRange(n: number, today = new Date()): { from: string; to: string } {
  const to = toUtc(localDayKey(today));
  return { from: toDay(to - (n - 1) * DAY_MS), to: toDay(to) };
}

/** Quarter start through today - never into the future: money that has
    not happened yet must not sit in a comparison or on a chart as $0. */
export function thisQuarterRange(today = new Date()): { from: string; to: string } {
  const q = Math.floor(today.getMonth() / 3);
  return {
    from: localDayKey(new Date(today.getFullYear(), q * 3, 1)),
    to: localDayKey(today),
  };
}

/** January 1 through today, for the same reason. */
export function thisYearRange(today = new Date()): { from: string; to: string } {
  return { from: `${today.getFullYear()}-01-01`, to: localDayKey(today) };
}

/** The stretch of the same length that ends the day before `from` -
    what "vs the prior period" compares against. */
export function previousRange(from: string, to: string): { from: string; to: string } {
  if (!isDayString(from) || !isDayString(to)) return { from, to };
  const span = toUtc(to) - toUtc(from) + DAY_MS;
  const prevTo = toUtc(from) - DAY_MS;
  return { from: toDay(prevTo - span + DAY_MS), to: toDay(prevTo) };
}
